import { useEffect } from "react";
import { generateClient } from "aws-amplify/api";
import { useQueryClient } from "@tanstack/react-query";
import * as subscriptions from "../graphql/subscriptions";

const client = generateClient();

const modelSubscriptions = Object.entries(subscriptions).filter(
  ([name, query]) =>
    /^on(Create|Update|Delete)/.test(name) && typeof query === "string"
);

/**
 * Keeps React Query views synchronized with changes from AppSync, including
 * writes made in another tab or by another administrator.
 */
const RealtimeQuerySync: React.FC = () => {
  const queryClient = useQueryClient();

  useEffect(() => {
    let refreshTimer: ReturnType<typeof setTimeout> | undefined;

    const scheduleRefresh = () => {
      if (refreshTimer) {
        clearTimeout(refreshTimer);
      }

      refreshTimer = setTimeout(() => {
        queryClient.invalidateQueries();
      }, 100);
    };

    const activeSubscriptions = modelSubscriptions.flatMap(([name, query]) => {
      try {
        const observable = client.graphql({ query: query as any }) as any;
        const subscription = observable.subscribe({
          next: scheduleRefresh,
          error: (error: unknown) => {
            console.warn(`Realtime sync unavailable for ${name}:`, error);
          },
        });

        return [subscription];
      } catch (error) {
        console.warn(`Unable to start realtime sync for ${name}:`, error);
        return [];
      }
    });

    return () => {
      if (refreshTimer) {
        clearTimeout(refreshTimer);
      }
      activeSubscriptions.forEach((subscription) => subscription.unsubscribe());
    };
  }, [queryClient]);

  return null;
};

export default RealtimeQuerySync;
